import {
  HHAlert,
  HHButton,
  HHDialog,
  HHDialogActions,
  HHDialogContent,
  HHDialogTitle,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { CreateConfigurationInput } from '../domain/types';
import { CopyToClipboard } from './copy-to-clipboard';

export const LIVE_EDIT_DIALOG_TEST_ID = 'pdg-configuration-live-edit-dialog';
export const LIVE_EDIT_DIALOG_CONFIRM_TEST_ID =
  'pdg-configuration-live-edit-dialog-confirm-button';
export const LIVE_EDIT_DIALOG_CANCEL_TEST_ID =
  'pdg-configuration-live-edit-dialog-cancel-button';
export const LIVE_EDIT_DIALOG_ERROR_TEST_ID =
  'pdg-configuration-live-edit-dialog-error-alert';
export const LIVE_EDIT_DIALOG_IDENTITY_WARNING_TEST_ID =
  'pdg-configuration-live-edit-dialog-identity-warning';

export const LIVE_EDIT_DIALOG_TITLE =
  'Save changes to a published configuration?';
export const LIVE_EDIT_DIALOG_MESSAGE =
  'This configuration is published. PDGS applies the change immediately: the next file from this provider is processed with the new version.';
export const LIVE_EDIT_DIALOG_IDENTITY_WARNING =
  'You are changing the name or data provider. Changing the data provider moves this configuration to a different provider: files from the current provider will stop matching it.';
export const LIVE_EDIT_DIALOG_CONFIRM_LABEL = 'Save to live configuration';
export const LIVE_EDIT_DIALOG_NO_HISTORY_MESSAGE =
  'PDGS keeps no history of earlier versions, so this cannot be undone from ops-hub. Copy the live version first if you might need to restore it.';
export const LIVE_EDIT_DIALOG_COPY_LIVE_LABEL = 'Copy the current live version';
export const LIVE_EDIT_DIALOG_COPY_LIVE_TEST_ID =
  'pdg-configuration-live-edit-dialog-copy-live-button';

const DIALOG_TITLE_ID = 'pdg-configuration-live-edit-dialog-title';
const DIALOG_MESSAGE_ID = 'pdg-configuration-live-edit-dialog-message';
const DIALOG_ERROR_ID = 'pdg-configuration-live-edit-dialog-error';
const DIALOG_CHANGES_ID = 'pdg-configuration-live-edit-dialog-changes';
const DIALOG_IDENTITY_ID = 'pdg-configuration-live-edit-dialog-identity';
const DIALOG_HISTORY_ID = 'pdg-configuration-live-edit-dialog-history';

export interface ConfigurationLiveEditConfirmationDialogProps {
  open: boolean;
  configurationName: string;
  changedFields: (keyof CreateConfigurationInput)[];
  /** The stored (pre-edit) configuration JSON, offered as a backup. */
  liveJson: string;
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation in front of saving a JSON edit to a PUBLISHED configuration.
 * PDGS has no status gate on update, so the edit is live as soon as it saves.
 * Same shape (and accessibility wiring) as the status confirmation dialog.
 */
export const ConfigurationLiveEditConfirmationDialog = ({
  open,
  configurationName,
  changedFields,
  liveJson,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfigurationLiveEditConfirmationDialogProps): JSX.Element => {
  const changesIdentity =
    changedFields.includes('name') || changedFields.includes('dataProvider');

  // Everything that decides whether to confirm is announced when the dialog
  // opens, not only the first paragraph.
  const describedBy = [
    DIALOG_MESSAGE_ID,
    DIALOG_CHANGES_ID,
    changesIdentity ? DIALOG_IDENTITY_ID : null,
    DIALOG_HISTORY_ID,
    error ? DIALOG_ERROR_ID : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <HHDialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby={DIALOG_TITLE_ID}
      aria-describedby={describedBy}
      data-testid={LIVE_EDIT_DIALOG_TEST_ID}
    >
      <HHDialogTitle id={DIALOG_TITLE_ID}>
        {LIVE_EDIT_DIALOG_TITLE}
      </HHDialogTitle>
      <HHDialogContent>
        <HHTypography
          hhVariant="variant-bypass"
          variant="body1"
          id={DIALOG_MESSAGE_ID}
          sx={{ marginBottom: 2 }}
        >
          {LIVE_EDIT_DIALOG_MESSAGE}
        </HHTypography>
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="textSecondary"
        >
          Configuration: <strong>{configurationName}</strong>
        </HHTypography>
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="textSecondary"
          id={DIALOG_CHANGES_ID}
        >
          Changing: <strong>{changedFields.join(', ')}</strong>
        </HHTypography>
        {changesIdentity && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="warning"
            id={DIALOG_IDENTITY_ID}
            data-testid={LIVE_EDIT_DIALOG_IDENTITY_WARNING_TEST_ID}
            sx={{ marginTop: 2 }}
          >
            {LIVE_EDIT_DIALOG_IDENTITY_WARNING}
          </HHAlert>
        )}
        <HHStack direction="row" alignItems="center" gap={1} marginTop={2}>
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            id={DIALOG_HISTORY_ID}
          >
            {LIVE_EDIT_DIALOG_NO_HISTORY_MESSAGE}
          </HHTypography>
          <CopyToClipboard
            data={liveJson}
            label={LIVE_EDIT_DIALOG_COPY_LIVE_LABEL}
            testId={LIVE_EDIT_DIALOG_COPY_LIVE_TEST_ID}
          />
        </HHStack>
        {error && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            id={DIALOG_ERROR_ID}
            data-testid={LIVE_EDIT_DIALOG_ERROR_TEST_ID}
            sx={{ marginTop: 2 }}
          >
            {error}
          </HHAlert>
        )}
      </HHDialogContent>
      <HHDialogActions>
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          disabled={loading}
          onClick={onCancel}
          data-testid={LIVE_EDIT_DIALOG_CANCEL_TEST_ID}
        >
          Cancel
        </HHButton>
        <HHButton
          hhVariant="variant-bypass"
          variant="contained"
          disabled={loading}
          onClick={onConfirm}
          data-testid={LIVE_EDIT_DIALOG_CONFIRM_TEST_ID}
        >
          {loading ? 'Saving…' : LIVE_EDIT_DIALOG_CONFIRM_LABEL}
        </HHButton>
      </HHDialogActions>
    </HHDialog>
  );
};
